import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatUGX } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Heart, Building2, LogOut, Loader2, ArrowRight } from "lucide-react";
import { toast } from "sonner";

export default function Profile() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [favs, setFavs] = useState(null);
  const [listings, setListings] = useState(null);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/login", { replace: true }); return; }
    let cancelled = false;
    (async () => {
      try {
        const [f, l] = await Promise.all([
          api.get("/me/favorites", { withCredentials: true }),
          api.get("/me/listings", { withCredentials: true }),
        ]);
        if (!cancelled) { setFavs(f.data); setListings(l.data); }
      } catch {
        if (!cancelled) toast.error("Could not load your data");
      }
    })();
    return () => { cancelled = true; };
  }, [user, loading, navigate]);

  if (loading || !user) {
    return (
      <main className="min-h-[60vh] grid place-items-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </main>
    );
  }

  return (
    <main className="max-w-6xl mx-auto px-6 md:px-12 py-12" data-testid="profile-page">
      <div className="flex items-center justify-between gap-6 mb-10 flex-wrap">
        <div className="flex items-center gap-4">
          {user.picture && <img src={user.picture} alt="" className="w-14 h-14 rounded-full border-2 border-primary/20" />}
          <div>
            <div className="eyebrow mb-1">My account</div>
            <h1 className="font-display text-3xl tracking-tighter" data-testid="profile-name">{user.name || user.email}</h1>
            <div className="text-sm text-muted-foreground font-mono">{user.email}</div>
          </div>
        </div>
        <Button variant="outline" onClick={async () => { await logout(); navigate("/"); }} className="rounded-full border-border" data-testid="profile-logout">
          <LogOut className="w-4 h-4" /> Sign out
        </Button>
      </div>

      <Tabs defaultValue="favorites">
        <TabsList className="bg-muted/60 border border-border rounded-full p-1" data-testid="profile-tabs">
          <TabsTrigger value="favorites" className="rounded-full" data-testid="tab-favorites">
            <Heart className="w-3.5 h-3.5" /> Favorites ({favs?.properties?.length ?? 0})
          </TabsTrigger>
          <TabsTrigger value="listings" className="rounded-full" data-testid="tab-mylistings">
            <Building2 className="w-3.5 h-3.5" /> My listings ({listings?.length ?? 0})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="favorites" className="mt-6">
          {!favs ? (
            <div className="text-muted-foreground py-10 text-center">Loading…</div>
          ) : favs.properties.length === 0 ? (
            <EmptyState
              title="No favorites yet"
              cta="Browse listings"
              onClick={() => navigate("/listings")}
              tid="empty-favs"
            />
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="favorites-grid">
              {favs.properties.map((p) => (
                <Link key={p.id} to={`/listings/${p.id}`} className="bg-card border border-border rounded-2xl overflow-hidden hover-lift" data-testid={`fav-${p.id}`}>
                  <img src={p.images?.[0]} alt="" className="aspect-[4/3] object-cover w-full" />
                  <div className="p-4">
                    <h3 className="font-display text-base font-semibold line-clamp-1">{p.title}</h3>
                    <div className="mt-1 text-xs text-muted-foreground">{p.district}, {p.region}</div>
                    <div className="mt-3 flex items-center justify-between">
                      <span className="font-display text-lg font-bold text-primary">{formatUGX(p.price_ugx)}</span>
                      <ArrowRight className="w-4 h-4 text-muted-foreground" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="listings" className="mt-6">
          {!listings ? (
            <div className="text-muted-foreground py-10 text-center">Loading…</div>
          ) : listings.length === 0 ? (
            <EmptyState
              title="You haven't listed anything yet"
              cta="List a property"
              onClick={() => navigate("/sell")}
              tid="empty-listings"
            />
          ) : (
            <div className="grid md:grid-cols-2 gap-4" data-testid="mylistings-grid">
              {listings.map((p) => (
                <div key={p.id} className="bg-card border border-border rounded-2xl p-5 flex gap-4 items-center" data-testid={`ml-${p.id}`}>
                  <img src={p.images?.[0]} alt="" className="w-24 h-24 rounded-xl object-cover border border-border" />
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground">{p.status} · {p.paid ? "PAID" : "unpaid"} · {p.package}</div>
                    <h3 className="font-display text-lg font-semibold line-clamp-1">{p.title}</h3>
                    <div className="mt-1 text-xs text-muted-foreground line-clamp-1">{p.district}, {p.region}</div>
                    <div className="mt-2 font-display text-lg font-bold text-primary">{formatUGX(p.price_ugx)}</div>
                  </div>
                  <Link to={`/listings/${p.id}`} className="text-primary text-sm shrink-0 hover:underline">View</Link>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </main>
  );
}

function EmptyState({ title, cta, onClick, tid }) {
  return (
    <div className="text-center py-16 bg-muted/30 rounded-2xl" data-testid={tid}>
      <h3 className="font-display text-2xl">{title}</h3>
      <Button onClick={onClick} className="mt-5 rounded-full bg-primary hover:bg-primary/90">{cta}</Button>
    </div>
  );
}
